import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { LinkButtonComponent } from "shared-ui-components/lines/linkButtonComponent";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { Attractor } from "core/Particles/attractor";
import { AttractorGridComponent } from "./attractorGridComponent";
import { SliderLineComponent } from "shared-ui-components/lines/sliderLineComponent";
import { Color3LineComponent } from "shared-ui-components/lines/color3LineComponent";
import { LineContainerComponent } from "shared-ui-components/lines/lineContainerComponent";
import { Color3, Color4 } from "core/Maths/math.color";
import { StandardMaterial } from "core/Materials/standardMaterial";
import { CreateSphere } from "core/Meshes/Builders/sphereBuilder";
import { GizmoManager } from "core/Gizmos/gizmoManager";
import { FontAsset } from "addons/msdfText/fontAsset";
import { TextRenderer } from "addons/msdfText/textRenderer";
import { Matrix } from "core/Maths/math.vector";
export class AttractorsGridComponent extends React.Component {
    constructor(props) {
        super(props);
        this._impostorMaterial = null;
        this._gizmoManager = null;
        this._sceneOnAfterRenderObserver = null;
        this._fontAsset = null;
        this.state = { impostorScale: 1, color: new Color3(1, 1, 1) };
    }
    addNewAttractor() {
        const particleSystem = this.props.host;
        const newAttractor = new Attractor();
        particleSystem.addAttractor(newAttractor);
        this.forceUpdate();
    }
    updateImpostorScale(value) {
        const particleSystem = this.props.host;
        const attractors = particleSystem.attractors;
        for (let i = 0; i < attractors.length; i++) {
            const attractor = attractors[i];
            const impostor = attractor._impostor;
            if (impostor) {
                impostor.scaling.setAll(value);
            }
        }
    }
    removeImpostor(attractor) {
        const impostor = attractor._impostor;
        if (impostor) {
            if (this._gizmoManager && this._gizmoManager.attachedMesh === impostor) {
                this._gizmoManager.attachToMesh(null);
            }
            impostor.dispose();
            attractor._impostor = null;
            const textRenderer = attractor._textRenderer;
            if (textRenderer) {
                textRenderer.dispose();
                attractor._textRenderer = null;
            }
        }
        const particleSystem = this.props.host;
        const attractors = particleSystem.attractors;
        for (let i = 0; i < attractors.length; i++) {
            if (attractors[i]._impostor) {
                return;
            }
        }
        if (this._impostorMaterial) {
            this._impostorMaterial.dispose();
            this._impostorMaterial = null;
        }
    }
    addImpostor(attractor, index) {
        const scene = this.props.host.getScene();
        if (!scene) {
            return;
        }
        const untypedAttractor = attractor;
        untypedAttractor._impostor = CreateSphere("Attractor impostor #" + index, { diameter: 1 }, scene);
        untypedAttractor._impostor.reservedDataStore = { hidden: true };
        untypedAttractor._impostor.scaling.setAll(this.state.impostorScale);
        untypedAttractor._impostor.position.copyFrom(attractor.position);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.addLabelAsync(attractor, index);
        if (!this._impostorMaterial) {
            this._impostorMaterial = new StandardMaterial("Attractor impostor material", scene);
            this._impostorMaterial.emissiveColor = this.state.color;
            this._impostorMaterial.disableLighting = true;
        }
        untypedAttractor._impostor.material = this._impostorMaterial;
        if (!this._sceneOnAfterRenderObserver) {
            const particleSystem = this.props.host;
            this._sceneOnAfterRenderObserver = scene.onAfterRenderObservable.add(() => {
                const attractors = particleSystem.attractors;
                for (let i = 0; i < attractors.length; i++) {
                    const impostor = attractors[i]._impostor;
                    if (!impostor) {
                        continue;
                    }
                    attractors[i].position.copyFrom(impostor.position);
                    const localUntypedAttractor = attractors[i];
                    if (localUntypedAttractor._textRenderer) {
                        localUntypedAttractor._textRenderer.color.r = this.state.color.r;
                        localUntypedAttractor._textRenderer.color.g = this.state.color.g;
                        localUntypedAttractor._textRenderer.color.b = this.state.color.b;
                        localUntypedAttractor._textRenderer.render(scene.getViewMatrix(), scene.getProjectionMatrix());
                    }
                }
            });
        }
    }
    async addLabelAsync(attractor, index) {
        const scene = this.props.host.getScene();
        if (!scene) {
            return;
        }
        const engine = scene.getEngine();
        if (!this._fontAsset) {
            const sdfFontDefinition = await (await fetch("https://assets.babylonjs.com/fonts/roboto-regular.json")).text();
            this._fontAsset = new FontAsset(sdfFontDefinition, "https://assets.babylonjs.com/fonts/roboto-regular.png");
        }
        const textRenderer = await TextRenderer.CreateTextRendererAsync(this._fontAsset, engine);
        textRenderer.addParagraph("#" + index, {}, Matrix.Scaling(0.5, 0.5, 0.5).multiply(Matrix.Translation(0, 1, 0)));
        textRenderer.isBillboard = true;
        textRenderer.color = Color4.FromColor3(this.state.color, 1.0);
        const untypedAttractor = attractor;
        textRenderer.parent = untypedAttractor._impostor;
        untypedAttractor._textRenderer = textRenderer;
    }
    controlImpostor(attractor, index) {
        const scene = this.props.host.getScene();
        if (!scene) {
            return;
        }
        const untypedAttractor = attractor;
        if (!untypedAttractor._impostor) {
            this.addImpostor(attractor, index);
        }
        if (!this._gizmoManager) {
            this._gizmoManager = new GizmoManager(scene);
            this._gizmoManager.positionGizmoEnabled = true;
            this._gizmoManager.attachableMeshes = [];
        }
        if (this._gizmoManager.attachedMesh === untypedAttractor._impostor) {
            this._gizmoManager.attachToMesh(null);
        }
        else {
            this._gizmoManager.attachToMesh(untypedAttractor._impostor);
        }
        this.forceUpdate();
    }
    shouldComponentUpdate(nextProps, nextState, nextContext) {
        if (this.props.host !== nextProps.host) {
            this.cleanup();
        }
        return true;
    }
    componentWillUnmount() {
        this.cleanup();
    }
    cleanup() {
        if (this._gizmoManager) {
            this._gizmoManager.dispose();
            this._gizmoManager = null;
        }
        const particleSystem = this.props.host;
        const attractors = particleSystem.attractors;
        for (let i = 0; i < attractors.length; i++) {
            const attractor = attractors[i];
            this.removeImpostor(attractor);
        }
        if (this._sceneOnAfterRenderObserver) {
            this.props.host.getScene()?.onAfterRenderObservable.remove(this._sceneOnAfterRenderObserver);
            this._sceneOnAfterRenderObserver = null;
        }
        if (this._fontAsset) {
            this._fontAsset.dispose();
            this._fontAsset = null;
        }
    }
    render() {
        const particleSystem = this.props.host;
        const attractors = particleSystem.attractors;
        return (_jsx("div", { children: _jsxs("div", { className: "gradient-container", children: [_jsx(LinkButtonComponent, { label: "", url: this.props.docLink, icon: faTrash, onIconClick: () => {
                            for (let i = 0; i < attractors.length; i++) {
                                particleSystem.removeAttractor(attractors[i]);
                            }
                            this.forceUpdate();
                        }, buttonLabel: "Add new attractor", onClick: () => this.addNewAttractor() }), attractors.map((a, i) => {
                        const codeRecorderPropertyName = `attractors[${i}]`;
                        return (_jsx(AttractorGridComponent, { globalState: this.props.globalState, host: this.props.host, codeRecorderPropertyName: codeRecorderPropertyName, lockObject: this.props.lockObject, lineIndex: i, attractor: a, isControlled: (a) => {
                                if (!this._gizmoManager) {
                                    return false;
                                }
                                return this._gizmoManager.attachedMesh === a._impostor;
                            }, addImpostor: (a, i) => this.addImpostor(a, i), removeImpostor: (a) => this.removeImpostor(a), onControl: (a, i) => this.controlImpostor(a, i), onDelete: (attractor) => {
                                this.props.host.removeAttractor(attractor);
                                this.forceUpdate();
                            } }, "step-" + i));
                    }), _jsxs(LineContainerComponent, { title: "IMPOSTORS", selection: this.props.globalState, children: [_jsx(SliderLineComponent, { lockObject: this.props.lockObject, label: "Size", directValue: this.state.impostorScale, minimum: 0, maximum: 10, step: 0.1, decimalCount: 1, onChange: (value) => {
                                    this.updateImpostorScale(value);
                                    this.setState({ impostorScale: value });
                                } }), _jsx(Color3LineComponent, { lockObject: this.props.lockObject, target: this.state, label: "Color", propertyName: "color", onChange: () => {
                                    if (this._impostorMaterial) {
                                        this._impostorMaterial.emissiveColor = this.state.color;
                                    }
                                    this.setState({ color: this.state.color });
                                } })] })] }) }));
    }
}
//# sourceMappingURL=attractorsGridComponent.js.map